/**
 * MobileBottomNav — persona-aware sticky bottom tab bar.
 *
 * Each user persona gets their own 5-tab thumb-zone:
 *
 *   • dog_owner   — Home · Dogs   · Log         · Inbox · Search   (+ FAB)
 *   • breeder     — Home · Dogs   · Litters     · Inbox · Search
 *   • transporter — Home · Trips  · Open bids   · Inbox · Search
 *   • buyer       — Home · Browse · Watchlist   · Inbox · Search
 *   • admin       — Home · Approvals · Disputes · Inbox · Search
 *
 * Persona resolved by `GET /api/me/nav-context`. When the user is on an
 * /admin/* route AND `is_admin === true`, we render the admin tabs.
 * Otherwise we render the resolved `primary` persona's tabs.
 *
 * Auto-hides on scroll-down, returns on scroll-up. Honors iOS safe-area.
 * Hidden on landing/login/reset routes.
 *
 * The Quick-Log FAB still exists but ONLY for `dog_owner` persona
 * (the only persona where one-tap `/care/log` posts make sense).
 */
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { HouseIcon as House, DogIcon as Dog, HeartStraightIcon as HeartStraight, EnvelopeIcon as Envelope, MagnifyingGlassIcon as MagnifyingGlass, PlusIcon as Plus, ForkKnifeIcon as ForkKnife, PersonSimpleRunIcon as PersonSimpleRun, DropIcon as Drop, GameControllerIcon as GameController, PillIcon as Pill, TruckIcon as Truck, ChartBarIcon as ChartBar, StorefrontIcon as Storefront, PawPrintIcon as PawPrint, ShieldStarIcon as ShieldStar, ScalesIcon as Scales } from "@phosphor-icons/react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError, thumbUrl } from "@/lib/utils-app";
import { isNativeApp } from "@/lib/nativeBridge";
import { haptic } from "@/lib/native";
import PersonaSwitcher, { readPersonaOverride } from "@/components/PersonaSwitcher";

const HIDDEN_ROUTES = new Set([
  "/", "/login", "/signup", "/forgot-password", "/reset-password",
]);

// Tab presets per persona. The "Log" tab (primary=true) gets the gold
// highlight + pill shape; everyone else is a plain icon+label.
const PRESETS = {
  dog_owner: [
    { to: "/dashboard", label: "Home",  icon: House,         testId: "btab-home" },
    { to: "/dogs",      label: "Dogs",  icon: Dog,           testId: "btab-dogs" },
    { to: "/dog-log",   label: "Log",   icon: HeartStraight, testId: "btab-log", primary: true },
    { to: "/inbox",     label: "Inbox", icon: Envelope,      testId: "btab-inbox", badgeKey: "unread" },
  ],
  breeder: [
    { to: "/dashboard", label: "Home",    icon: House,    testId: "btab-home" },
    { to: "/dogs",      label: "Dogs",    icon: Dog,      testId: "btab-dogs" },
    { to: "/litters",   label: "Litters", icon: PawPrint, testId: "btab-litters", primary: true },
    { to: "/inbox",     label: "Inbox",   icon: Envelope, testId: "btab-inbox", badgeKey: "unread" },
  ],
  transporter: [
    { to: "/dashboard",  label: "Home",  icon: House,    testId: "btab-home" },
    { to: "/transporter", label: "Trips", icon: Truck,   testId: "btab-trips", primary: true },
    { to: "/transporter?focus=bids", label: "Bids", icon: ChartBar, testId: "btab-bids" },
    { to: "/inbox",      label: "Inbox", icon: Envelope, testId: "btab-inbox", badgeKey: "unread" },
  ],
  international_transporter: [
    { to: "/dashboard", label: "Home",      icon: House,    testId: "btab-home" },
    { to: "/transport/international", label: "Directory", icon: Truck, testId: "btab-intl-directory", primary: true },
    { to: "/inbox",     label: "Inbox",     icon: Envelope, testId: "btab-inbox", badgeKey: "unread" },
  ],
  buyer: [
    { to: "/dashboard",   label: "Home",   icon: House,      testId: "btab-home" },
    { to: "/marketplace", label: "Browse", icon: Storefront, testId: "btab-browse", primary: true },
    { to: "/offers/my",   label: "Offers", icon: HeartStraight, testId: "btab-offers" },
    { to: "/inbox",       label: "Inbox",  icon: Envelope,   testId: "btab-inbox", badgeKey: "unread" },
  ],
  service_provider: [
    { to: "/dashboard",  label: "Home",     icon: House,      testId: "btab-home" },
    { to: "/provider/me", label: "Profile", icon: ShieldStar, testId: "btab-profile" },
    { to: "/my-bookings", label: "Bookings", icon: HeartStraight, testId: "btab-bookings", primary: true },
    { to: "/inbox",      label: "Inbox",    icon: Envelope,   testId: "btab-inbox", badgeKey: "unread" },
  ],
  storefront_seller: [
    { to: "/dashboard", label: "Home",    icon: House,      testId: "btab-home" },
    { to: "/me/store",  label: "Store",   icon: Storefront, testId: "btab-store", primary: true },
    { to: "/me/store?tab=orders", label: "Orders", icon: ChartBar, testId: "btab-orders" },
    { to: "/inbox",     label: "Inbox",   icon: Envelope,   testId: "btab-inbox", badgeKey: "unread" },
  ],
  admin: [
    { to: "/admin",                    label: "Home",      icon: House,      testId: "btab-home" },
    { to: "/admin/approvals",          label: "Approvals", icon: ShieldStar, testId: "btab-approvals", primary: true, badgeKey: "admin" },
    { to: "/admin/transport-disputes", label: "Disputes",  icon: Scales,     testId: "btab-disputes" },
    { to: "/inbox",                    label: "Inbox",     icon: Envelope,   testId: "btab-inbox", badgeKey: "unread" },
  ],
};

export default function MobileBottomNav({ user, unread, onOpenSearch }) {
  const location = useLocation();
  const [visible, setVisible] = useState(true);
  const lastY = useRef(typeof window !== "undefined" ? window.scrollY : 0);
  const [nav, setNav] = useState(null);
  const [override, setOverride] = useState(() => readPersonaOverride());

  // Resolve persona once on mount; refresh on auth change.
  useEffect(() => {
    if (!user) { setNav(null); return; }
    let cancelled = false;
    api.get("/me/nav-context", { silent: true })
      .then((r) => {
        if (!cancelled) setNav(r.data || null);
      })
      .catch(() => {
        if (!cancelled) setNav({ primary: "breeder", available: ["breeder"], is_admin: false, signals: {} });
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  // Listen for override changes from PersonaSwitcher.
  useEffect(() => {
    const onChange = (e) => setOverride(e?.detail?.persona || null);
    window.addEventListener("persona-override-changed", onChange);
    return () => window.removeEventListener("persona-override-changed", onChange);
  }, []);

  // Auto-hide on scroll-down, show on scroll-up.
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (y < 80) { setVisible(true); lastY.current = y; return; }
      const delta = y - lastY.current;
      if (Math.abs(delta) < 8) return;
      setVisible(delta < 0);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!user) return null;
  if (HIDDEN_ROUTES.has(location.pathname)) return null;
  if (!nav) return null;

  // Render admin nav when admin user is currently inside /admin/* surface
  // (cannot be overridden — admins inside admin pages always see admin nav).
  const inAdminMode = !!nav.is_admin && location.pathname.startsWith("/admin");
  // Honor user override IF it's still in the available list (otherwise
  // their data signals have changed and we silently fall back to primary).
  const overrideValid = override && (nav.available || []).includes(override);
  // Default-persona rule for admins: show admin tabs EVERYWHERE on mobile,
  // not just under /admin/*. This lets an admin acknowledge approvals,
  // handle disputes, etc. from any page without scrolling back to admin
  // home first. Admins who'd rather have breeder/dog_owner shortcuts can
  // flip via the persona-switcher chip (override wins for those).
  const persona = inAdminMode
    ? "admin"
    : (overrideValid
        ? override
        : (nav.is_admin ? "admin" : (nav.primary || "breeder")));
  const tabs = PRESETS[persona] || PRESETS.breeder;
  // Native app: the tab bar is the primary navigation on EVERY size
  // class (iPhone + iPad). Web keeps it mobile-only.
  const sizeClass = isNativeApp() ? "" : "md:hidden";

  return (
    <>
      {/* Spacer so the fixed nav doesn't cover the last bit of content.
          Auto-grows when a persona switcher chip is also showing (the
          chip sits above the tab bar and bloats the fixed footer to
          ~120px). Honor iOS safe-area-inset on top of that. */}
      <div
        className={sizeClass}
        style={{
          height: (!inAdminMode && (nav.available || []).length > 1 ? 128 : 88),
        }}
        aria-hidden="true"
      />

      <nav
        data-testid="mobile-bottom-nav"
        data-persona={persona}
        className={`${sizeClass} fixed left-0 right-0 bottom-0 z-40 transition-transform duration-200 ${
          visible ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {/* PersonaSwitcher chip — only renders when user has ≥2 available
            personas. Sits above the tab bar so the bottom-nav layout is
            untouched and the chip never crowds the tab targets. */}
        {!inAdminMode && (nav.available || []).length > 1 && (
          <div className="px-3 pb-1.5 flex justify-center">
            <PersonaSwitcher
              available={nav.available}
              current={persona}
              variant="floating"
            />
          </div>
        )}
        <div className="mx-2 mb-2 rounded-2xl bg-[#FBF7EC]/95 backdrop-blur-xl border border-[#C9A227]/30 shadow-[0_-2px_24px_rgba(193,162,39,0.12)] relative overflow-hidden">
          <div className="absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent via-[#C9A227]/45 to-transparent pointer-events-none" aria-hidden="true" />
          <div className="grid grid-cols-5 items-stretch">
            {tabs.map((t) => (
              <NavLink
                key={t.testId}
                to={t.to}
                data-testid={t.testId}
                end={t.to === "/admin"}
                className={({ isActive }) =>
                  `relative flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-bold uppercase tracking-[0.08em] ${
                    isActive
                      ? t.primary
                        ? "text-gold-dark"
                        : "text-ink"
                      : "text-[#6B6B6B] hover:text-ink"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {t.primary ? (
                      <span
                        className={`inline-flex items-center justify-center h-9 w-9 rounded-full transition-colors ${
                          isActive
                            ? "bg-gold text-ink"
                            : "bg-[#FBF7EC] text-gold-dark border border-[#E8D9A6]"
                        }`}
                      >
                        <t.icon size={18} weight={isActive ? "fill" : "duotone"} />
                      </span>
                    ) : (
                      <span className="relative inline-flex items-center justify-center h-6 w-6">
                        <t.icon size={20} weight={isActive ? "fill" : "duotone"} />
                        {t.badgeKey === "unread" && unread > 0 && (
                          <span
                            data-testid="btab-inbox-badge"
                            className="absolute -top-1 -right-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-[#C1272D] text-white text-[9px] font-bold leading-none"
                          >
                            {unread > 99 ? "99+" : unread}
                          </span>
                        )}
                      </span>
                    )}
                    <span>{t.label}</span>
                  </>
                )}
              </NavLink>
            ))}
            {/* Fifth slot — Search (universal) */}
            <button
              type="button"
              onClick={onOpenSearch}
              data-testid="btab-search"
              className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-[#6B6B6B] hover:text-ink"
            >
              <span className="inline-flex items-center justify-center h-6 w-6">
                <MagnifyingGlass size={20} weight="duotone" />
              </span>
              <span>Search</span>
            </button>
          </div>
        </div>
      </nav>

      {/* FAB only for dog_owner persona */}
      {persona === "dog_owner" && (
        <QuickLogFab visible={visible} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// QuickLogFab — floating "+ Log" button on mobile only.
// Rendered only for the `dog_owner` persona since it posts to /care/log.
// ---------------------------------------------------------------------------

const QUICK_ACTIONS = [
  { kind: "meal",   label: "Meal",  icon: ForkKnife,        color: "#7C5BFF" },
  { kind: "walk",   label: "Walk",  icon: PersonSimpleRun,  color: "#0EA5A0" },
  { kind: "potty",  label: "Potty", icon: Drop,             color: "#3B82F6" },
  { kind: "play",   label: "Play",  icon: GameController,   color: "#F59E0B" },
  { kind: "meds",   label: "Med",   icon: Pill,             color: "#C1272D" },
];

function QuickLogFab({ visible }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [eligible, setEligible] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [selectedDogId, setSelectedDogId] = useState(null);
  const [savingKind, setSavingKind] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/care/status", { silent: true }).catch(() => ({ data: { active: false } })),
      api.get("/dogs?limit=100", { silent: true }).catch(() => ({ data: [] })),
    ]).then(([s, d]) => {
      if (cancelled) return;
      const rows = Array.isArray(d.data) ? d.data : (d.data?.dogs || []);
      const living = rows.filter((dg) => !dg.deceased_at);
      const isActive = !!s.data?.active && living.length > 0;
      setEligible(isActive);
      setDogs(living);
      if (living.length > 0) setSelectedDogId(living[0].id);
    });
    return () => { cancelled = true; };
  }, []);

  const HIDE_ON = ["/dog-log", "/login", "/signup", "/", "/forgot-password", "/reset-password"];
  if (HIDE_ON.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))) return null;
  if (eligible !== true) return null;

  const logQuick = async (kind) => {
    if (!selectedDogId) return;
    setSavingKind(kind);
    try {
      await api.post("/care/log", { dog_id: selectedDogId, kind });
      toast.success(`${QUICK_ACTIONS.find((a) => a.kind === kind)?.label || "Entry"} logged`);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't log that — try again");
    } finally {
      setSavingKind(null);
    }
  };

  const selectedDog = dogs.find((d) => d.id === selectedDogId);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="quick-log-fab"
          aria-label="Quick log"
          className={`${isNativeApp() ? "" : "md:hidden"} fixed right-4 z-50 inline-flex items-center justify-center h-14 w-14 rounded-full bg-ink text-gold shadow-[0_8px_24px_rgba(0,0,0,0.3)] active:scale-95 transition-all ${
            visible ? "bottom-24" : "bottom-6"
          }`}
          style={{ marginBottom: "env(safe-area-inset-bottom)" }}
        >
          <Plus size={24} weight="bold" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="rounded-t-2xl border-t border-[#E5E5E5] p-0 max-h-[80vh]"
        data-testid="quick-log-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#E5E5E5]/70">
          <SheetTitle className="font-spectral text-2xl tracking-tight text-left">
            Quick log
          </SheetTitle>
          <SheetDescription className="text-left text-[#6B6B6B] text-sm">
            One tap to record an entry. Add notes later in Dog Log.
          </SheetDescription>
        </SheetHeader>

        {dogs.length > 1 && (
          <div className="px-5 py-3 border-b border-[#E5E5E5]/70 flex items-center gap-3 overflow-x-auto scrollbar-none">
            {dogs.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setSelectedDogId(d.id)}
                data-testid={`quick-log-dog-${d.id}`}
                className={`flex items-center gap-2 flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border transition-colors ${
                  selectedDogId === d.id
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-[#3F3F3F] border-[#E5E5E5] hover:border-ink"
                }`}
              >
                {d.photo_url && (
                  <img
                    src={thumbUrl(d.photo_url)}
                    alt=""
                    className="h-5 w-5 rounded-full object-cover -ml-1"
                  />
                )}
                {d.name}
              </button>
            ))}
          </div>
        )}

        <div className="px-5 pt-4 pb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#6B6B6B]">
          {selectedDog ? `Logging for ${selectedDog.name}` : "Pick an action"}
        </div>
        <div className="grid grid-cols-3 gap-2 px-4 pb-4">
          {QUICK_ACTIONS.map((a) => (
            <button
              key={a.kind}
              type="button"
              onClick={() => logQuick(a.kind)}
              disabled={savingKind === a.kind}
              data-testid={`quick-log-${a.kind}`}
              className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-[#E5E5E5] bg-white hover:bg-[#FAFAFA] py-4 active:scale-95 transition-all disabled:opacity-50"
            >
              <span
                className="inline-flex items-center justify-center h-10 w-10 rounded-full"
                style={{ backgroundColor: `${a.color}15` }}
              >
                <a.icon size={20} weight="duotone" style={{ color: a.color }} />
              </span>
              <span className="text-[12px] font-semibold text-ink">{a.label}</span>
              <span className="text-[10px] text-[#6B6B6B]">
                {savingKind === a.kind ? "Logging…" : "Tap to log"}
              </span>
            </button>
          ))}
        </div>

        <div className="px-4 pb-5">
          <Button
            variant="outline"
            className="w-full rounded-full border-[#E5E5E5] h-11"
            onClick={() => {
              setOpen(false);
              navigate(selectedDogId ? `/dog-log?dog_id=${selectedDogId}` : "/dog-log");
            }}
            data-testid="quick-log-open-full"
          >
            Open full Dog Log →
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
